import moment from "moment";

import {
  GeneralGeolocationInputDto,
  GeoJsonPointDto,
  GeoJsonPointInputDto,
} from "@/core/api/generated/v1";

import {
  GeoMarker,
  GeoMarkerOptions,
  GeoPointCoordinates,
  GeoPointCoordinatesAsList as GeoPointCoordinatesAsArray,
} from "../ts/geo";
import { IdHelper } from "./id";
import { TypeHelper } from "./type";

/** Geolocation, etc. */
export class GeoHelper {
  static readonly longitudeMin = -180;
  static readonly longitudeMax = 180;
  static readonly latitudeMin = -90;
  static readonly latitudeMax = 90;
  /** Map zoom presets for Azure Maps. */
  static readonly mapZoomPresets = {
    world: 0,
    mainland: 2,
    country: 4,
    countryRegion: 6,
    city: 9,
    cityArea: 12,
    cityDistrict: 14,
    street: 15,
    building: 18,
    spot: 20,
  };

  static isValidLongitude(value: Nil<number>): value is number {
    return (
      !TypeHelper.isNil(value) &&
      TypeHelper.isNumber(value) &&
      value >= this.longitudeMin &&
      value <= this.longitudeMax
    );
  }

  static isValidLatitude(value: Nil<number>): value is number {
    return (
      !TypeHelper.isNil(value) &&
      TypeHelper.isNumber(value) &&
      value >= this.latitudeMin &&
      value <= this.latitudeMax
    );
  }

  static isValidPointCoordinates(
    value: Nil<Partial<GeoPointCoordinates>>,
  ): value is GeoPointCoordinates {
    return (
      !!value && this.isValidLongitude(value.longitude) && this.isValidLatitude(value.latitude)
    );
  }

  static clampLongitude(value: number): number {
    return value < this.longitudeMin
      ? this.longitudeMin
      : value > this.longitudeMax
        ? this.longitudeMax
        : value;
  }

  static clampLatitude(value: number): number {
    return value < this.latitudeMin
      ? this.latitudeMin
      : value > this.latitudeMax
        ? this.latitudeMax
        : value;
  }

  static getCoordinatesAsArray(value: GeoPointCoordinates): GeoPointCoordinatesAsArray {
    return [value.longitude, value.latitude];
  }

  static getCoordinatesAsObject(value: GeoPointCoordinatesAsArray): GeoPointCoordinates {
    return { longitude: value[0], latitude: value[1] };
  }

  static getCoordinatesAsString(value: GeoPointCoordinates): string {
    return `${value.latitude}, ${value.longitude}`;
  }

  static createMarkerFromGeoPoint(params: {
    point: Nil<GeoJsonPointDto | GeoJsonPointInputDto>;
    markerOptions?: Partial<GeoMarkerOptions>;
  }): Nil<GeoMarker> {
    return params.point && this.isValidPointCoordinates(params.point)
      ? {
          id: IdHelper.newUuid4(),
          coordinates: params.point,
          options: {
            ...params.markerOptions,
          },
        }
      : undefined;
  }

  static prepareGeneralGeolocationInputDtoForApi(
    value: Nil<GeneralGeolocationInputDto>,
  ): Nil<GeneralGeolocationInputDto> {
    const point =
      value && this.isValidPointCoordinates(value.point)
        ? {
            ...value.point,
            longitude: this.isValidLongitude(value.point.longitude)
              ? value.point.longitude
              : undefined!,
            latitude: this.isValidLatitude(value.point.latitude)
              ? value.point.latitude
              : undefined!,
          }
        : undefined;
    return value && point
      ? {
          ...value,
          point: point,
          capturedAt: value.capturedAt ? moment(value.capturedAt).utc().format() : undefined,
        }
      : undefined;
  }
}
